import type { ToolInvocationUIPart } from '@ai-sdk/ui-utils';
import { AnimatePresence, motion } from 'framer-motion';
import { AssistantThinking } from '~/components/chat/assistant-message/AssistantThinking';
import { useDetailsDialog } from '~/components/chat/assistant-message/context';
import { ToolCallText } from '~/components/chat/assistant-message/message-parts/ToolCallText';
import type { TodoGroupsData } from '~/components/chat/assistant-message/types';
import { isTodoWritePart, isToolInvocationPart } from '~/lib/ai';
import { useCurrentMessage } from '~/lib/context/message';
import type { AssistantMessageTodo, AssistantMessageTodoStatus } from '~/types/chat';
import { classNames } from '~/utils/classNames';
import { SUPABASE_DATABASE_REGEX } from '~/utils/constants';

interface PlanProps {
  part: ToolInvocationUIPart;
  todoGroupsData: TodoGroupsData;
}

const statusColors: Record<AssistantMessageTodoStatus, string> = {
  pending: 'text-bolt-ds-textSecondary',
  in_progress: 'text-bolt-ds-brand',
  completed: 'text-bolt-ds-success',
  cancelled: 'text-bolt-ds-iconSecondary',
};

const statusIcons: Record<AssistantMessageTodoStatus, string> = {
  pending: 'i-bolt:circle',
  in_progress: 'i-bolt:circle-notch animate-spin',
  completed: 'i-heroicons-outline:check-circle',
  cancelled: 'i-heroicons-outline:x-circle',
};

export function Plan({ part, todoGroupsData }: PlanProps) {
  const message = useCurrentMessage();
  const { openDetailsDialog } = useDetailsDialog();

  // get the parent context of this TodoWrite
  const partContext = (part.toolInvocation as any).parentToolUseId || null;

  const contextData = todoGroupsData.contextMap.get(partContext);

  // use todos from the latest TodoWrite in this context
  const rawTodos = (contextData?.latestTodoWritePart?.toolInvocation.args.todos ||
    part.toolInvocation.args.todos ||
    []) as AssistantMessageTodo[];

  const todos = rawTodos.filter((todo) => {
    return todo != null && todo.status != null && todo.content != null;
  });

  const isPlanCompleted = todos.every((todo) => todo.status === 'completed' || todo.status === 'cancelled');

  const partIndex = todoGroupsData.contextMap.get(partContext)?.latestTodoWriteIndex ?? -1;

  // find the last non-TodoWrite tool invocation that comes after this TodoWrite in the same context
  const laterToolInvocationParts = message?.parts
    .slice((partIndex === -1 ? 0 : partIndex) + 1)
    .filter(isToolInvocationPart)
    .filter((laterPart) => !isTodoWritePart(laterPart))
    .filter((laterPart) => {
      const laterContext = (laterPart.toolInvocation as any).parentToolUseId || null;
      return laterContext === partContext;
    });

  const toolInvocation = laterToolInvocationParts?.at(-1)?.toolInvocation;

  const formattedTodos = todos.map((todo, index) => {
    let status = todo.status;

    if (message?.status === 'incomplete') {
      status = 'cancelled';
    } else if (message?.status === 'streaming' && index === 0 && todo.status === 'pending') {
      status = 'in_progress';
    }

    const icon = statusIcons[status];
    const iconColor = message?.status !== 'incomplete' ? statusColors[status] : 'text-bolt-ds-iconSecondary';
    const isLast = index === todos.length - 1;
    const isTodoInProgress = status === 'in_progress';

    const showStatus = message?.status !== 'incomplete' && (isTodoInProgress || (isPlanCompleted && isLast));

    const content = todo.content.replace(SUPABASE_DATABASE_REGEX, 'Bolt Database');
    const activeForm = todo.activeForm?.replace(SUPABASE_DATABASE_REGEX, 'Bolt Database');

    return {
      content,
      activeForm,
      status,
      icon,
      iconColor,
      showStatus,
      displayContent: status === 'in_progress' ? activeForm || content : content,
    };
  });

  return (
    <div className="bg-bolt-ds-surfaceOne rounded-md overflow-clip">
      <div className="flex gap-2 px-3 pt-2">
        <i className="size-4.5 shrink-0 i-heroicons-solid:list-bullet text-bolt-elements-icon-secondary group-hover/button:text-bolt-ds-textPrimary transition-theme" />
        <div>Plan</div>
      </div>

      <div className="bg-bolt-ds-surfaceOne m-px rounded-[calc(theme('borderRadius.md')-1px)] p-3 space-y-2.5">
        {formattedTodos.map((todo) => (
          <div key={todo.content} className="relative">
            <div
              className={classNames('flex items-start gap-2 text-sm transition-opacity duration-200', {
                'opacity-40 line-through': todo.status === 'cancelled',
                'opacity-40': todo.status === 'pending',
              })}
            >
              <div className="relative size-4.5 shrink-0">
                <AnimatePresence>
                  <motion.div
                    key={todo.status}
                    initial={{ opacity: 0, scale: 0.75, filter: 'blur(12px)' }}
                    animate={{ opacity: 1, scale: 1, filter: 'blur(0px)' }}
                    exit={{ opacity: 0, scale: 0.75, filter: 'blur(12px)' }}
                    transition={{ type: 'easeOut', duration: 0.5 }}
                    className={classNames('size-4.5 shrink-0 inset-0 absolute left-0 mt-px', todo.icon, todo.iconColor)}
                  />
                </AnimatePresence>
              </div>

              <div>
                <span
                  role="button"
                  className="hover:underline cursor-pointer"
                  onClick={() => openDetailsDialog(btoa(todo.content))}
                >
                  {todo.displayContent}
                </span>
              </div>
            </div>

            {todo.showStatus && todo.status === 'in_progress' && (
              <span className="absolute top-7 left-2 -ml-px w-4 bottom-2.5 border-l-2 border-b-2 border-bolt-ds-iconSecondary opacity-30 rounded-bl-md"></span>
            )}

            <motion.div
              initial={false}
              animate={{
                height: todo.showStatus ? 'auto' : 0,
                marginTop: todo.showStatus ? 8 : 0,
              }}
              transition={{ type: 'easeOut', duration: 0.3 }}
            >
              <AnimatePresence>
                {todo.showStatus && (
                  <div className={todo.status === 'in_progress' ? 'relative ml-8' : 'relative'}>
                    <AnimatePresence mode="popLayout">
                      <motion.div
                        initial={{ y: 20, opacity: 0, scale: 0.75, filter: 'blur(12px)' }}
                        animate={{ y: 0, opacity: 1, scale: 1, filter: 'blur(0px)' }}
                        exit={{ y: -20, opacity: 0, scale: 0.75, filter: 'blur(12px)' }}
                        transition={{ type: 'easeOut', duration: 0.25 }}
                        className="flex items-center gap-2 whitespace-nowrap"
                        style={{ originX: 0 }}
                        key={isPlanCompleted ? 'completed' : toolInvocation?.toolCallId}
                      >
                        {isPlanCompleted && (
                          <div
                            className="flex items-center gap-2 pt-2 group w-full cursor-pointer"
                            role="button"
                            onClick={() => openDetailsDialog()}
                          >
                            <span className="i-bolt:check-double size-4.5 shrink-0 col-start-1 row-start-1 text-bolt-ds-success"></span>
                            <div className="group-hover:underline leading-6">Plan completed</div>
                            <div className="flex-1"></div>
                            <span className="opacity-0 group-hover:opacity-100 font-normal text-size-xs transition-[opacity,transform] duration-200 text-bolt-ds-brand">
                              Open details
                            </span>
                          </div>
                        )}

                        {!isPlanCompleted && !toolInvocation && (
                          <div
                            className="flex items-center gap-2 group w-full cursor-pointer"
                            role="button"
                            onClick={() => openDetailsDialog()}
                          >
                            <span className="i-heroicons-outline:light-bulb size-4.5 shrink-0 -translate-y-px text-bolt-ds-iconSecondary"></span>
                            <AssistantThinking className="h-6" />
                          </div>
                        )}

                        {!isPlanCompleted && toolInvocation && (
                          <ToolCallText toolInvocation={toolInvocation} isPending={todo.status === 'in_progress'} />
                        )}
                      </motion.div>
                    </AnimatePresence>
                  </div>
                )}
              </AnimatePresence>
            </motion.div>
          </div>
        ))}
      </div>
    </div>
  );
}
